import { useMemo, useState } from 'react';
import { Check, RotateCcw, X } from 'lucide-react';

/**
 * Édition de la fréquence d'un batch.
 *
 * Une expression cron est facile à saisir de travers, et une erreur ne se voit
 * pas : le batch cesse simplement de tourner. Deux garde-fous, donc :
 *   • des choix courants proposés en un clic, pour n'avoir à écrire du cron
 *     que dans les cas qui le méritent ;
 *   • un APERÇU des prochaines occurrences avant validation — c'est la seule
 *     vérification qui parle vraiment à un lecteur humain.
 *
 * Le back valide de nouveau et refuse une expression illisible : cet aperçu
 * est un confort de saisie, pas la garantie.
 */

/** Convention de l'écosystème : 6 champs, secondes en tête. */
const PRESETS = [
  { label: 'Chaque jour à 08 h 30', value: '0 30 8 * * *' },
  { label: '3× / jour (8 h 30, 14 h 30, 20 h 30)', value: '0 30 8,14,20 * * *' },
  { label: 'Chaque heure', value: '0 0 * * * *' },
  { label: 'Lundi au vendredi à 07 h 00', value: '0 0 7 * * 1-5' },
];

/**
 * Aperçu local des prochaines occurrences.
 *
 * Volontairement limité aux formes simples (valeurs fixes ou listes) : plutôt
 * que de réimplémenter un moteur cron dans le navigateur — et de risquer un
 * aperçu FAUX, pire que pas d'aperçu — on ne prévisualise que ce qu'on sait
 * calculer avec certitude, et on l'annonce quand on ne sait pas.
 */
function previewNext(expression, count = 3) {
  const parts = expression.trim().split(/\s+/);
  if (parts.length !== 6) return null;
  const [sec = '', min = '', hour = '', dom = '', mon = '', dow = ''] = parts;
  if (sec !== '0' || dom !== '*' || mon !== '*') return null;
  if (!/^\d{1,2}$/.test(min)) return null;

  const hours = /^\*$/.test(hour)
    ? Array.from({ length: 24 }, (_, i) => i)
    : /^[\d,]+$/.test(hour)
      ? hour.split(',').map(Number)
      : null;
  if (!hours || hours.some((h) => Number.isNaN(h) || h > 23)) return null;

  const days = /^\*$/.test(dow)
    ? null
    : /^(\d)-(\d)$/.test(dow)
      ? (() => {
          const [, a, b] = /^(\d)-(\d)$/.exec(dow);
          const out = [];
          for (let i = Number(a); i <= Number(b); i += 1) out.push(i % 7);
          return out;
        })()
      : /^[\d,]+$/.test(dow)
        ? dow.split(',').map((d) => Number(d) % 7)
        : null;
  if (dow !== '*' && !days) return null;

  const out = [];
  const now = new Date();
  for (let d = 0; d < 14 && out.length < count; d += 1) {
    const day = new Date(now);
    day.setDate(now.getDate() + d);
    if (days && !days.includes(day.getDay())) continue;
    for (const h of [...hours].sort((a, b) => a - b)) {
      const when = new Date(day);
      when.setHours(h, Number(min), 0, 0);
      if (when > now && out.length < count) out.push(when);
    }
  }
  return out;
}

export default function CronEditor({ current, timezone, onCancel, onSubmit, busy }) {
  const [value, setValue] = useState(current ?? '');
  const preview = useMemo(() => previewNext(value), [value]);
  const shapeOk = value.trim().split(/\s+/).length === 6;
  const changed = value.trim() !== (current ?? '').trim();

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {PRESETS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => setValue(p.value)}
            style={{
              padding: '4px 11px',
              borderRadius: 'var(--radius-pill)',
              border: `1px solid ${value === p.value ? 'var(--brand)' : 'var(--glass-border)'}`,
              background: value === p.value ? 'var(--brand)' : 'transparent',
              color: value === p.value ? '#fff' : 'var(--text-muted)',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
        Expression cron — 6 champs : secondes minutes heures jour mois jour-semaine
      </label>
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        spellCheck={false}
        placeholder="0 30 8 * * *"
        style={{
          width: '100%',
          padding: '8px 10px',
          borderRadius: 'var(--radius-sm)',
          border: `1px solid ${shapeOk || !value ? 'var(--glass-border)' : 'var(--danger)'}`,
          background: 'var(--bg)',
          color: 'var(--text)',
          fontFamily: 'ui-monospace, monospace',
          fontSize: 13,
        }}
      />

      <div style={{ minHeight: 46, marginTop: 8, fontSize: 12 }}>
        {!shapeOk && value ? (
          <span style={{ color: 'var(--danger)' }}>
            Il faut exactement 6 champs séparés par des espaces.
          </span>
        ) : preview?.length ? (
          <>
            <span style={{ color: 'var(--text-muted)' }}>Prochaines occurrences ({timezone}) :</span>
            <div style={{ color: 'var(--text)' }}>
              {preview.map((d) => d.toLocaleString('fr-CH', { dateStyle: 'medium', timeStyle: 'short' })).join(' · ')}
            </div>
          </>
        ) : shapeOk && value ? (
          <span style={{ color: 'var(--text-muted)' }}>
            Aperçu indisponible pour cette expression — elle sera validée par le serveur, qui
            affichera les occurrences réelles après enregistrement.
          </span>
        ) : null}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
        <button
          type="button"
          disabled={busy || !shapeOk || !changed}
          onClick={() => onSubmit(value.trim())}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '7px 14px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--brand)',
            background: 'var(--brand)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: busy || !shapeOk || !changed ? 'not-allowed' : 'pointer',
            opacity: busy || !shapeOk || !changed ? 0.55 : 1,
          }}
        >
          <Check size={14} /> Enregistrer
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onSubmit(null)}
          title="Rétablir la fréquence définie dans le code"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '7px 14px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--glass-border)',
            background: 'transparent',
            color: 'var(--text)',
            fontSize: 13,
            cursor: busy ? 'not-allowed' : 'pointer',
          }}
        >
          <RotateCcw size={14} /> Valeur par défaut
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '7px 14px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid transparent',
            background: 'transparent',
            color: 'var(--text-muted)',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          <X size={14} /> Annuler
        </button>
      </div>
    </div>
  );
}
